// frontend/src/pages/sales/orderDiscountRules.ts
//
// 🔒 Manak 2026-10-03 — ONE set of web discount rules for the proposal editor,
// Quick Quote and the Sales Order editor. A client-side courtesy that mirrors
// the server gate so the rep finds out BEFORE saving; the server stays the
// authority (gate-and-hold.ts, policy-line-gate.ts, gate.ts).
//
//   LINE discount — the chain:
//     ≤ the rep's self-serve (Slab 1)            → ok, saves
//     above self-serve, ≤ the product's own max  → saves, HELD for approval
//                                                  (team head → MD)
//     above the product's own max                → REFUSED, nobody can clear it
//
//   ORDER (additional) discount — ruling 2026-08-27, NO approval step:
//     ≤ the order's limit (MIN of the lines' maxAdditionalDiscountPct, 7.5 when
//       nothing resolves)                        → allowed
//     above it                                   → REFUSED
//     any line whose category allows none        → field disabled, says which

export const DEFAULT_ADDITIONAL_ALLOWANCE_PCT = 7.5;

/**
 * The order's additional-discount limit: MIN of maxAdditionalDiscountPct across
 * the lines that resolve; nulls and lines that allow none are skipped; 7.5 when
 * nothing resolves. Mirrors gate.ts resolveAdditionalAllowancePct.
 */
export function resolveAdditionalAllowanceCeiling(
  keys: ReadonlyArray<string | null | undefined>,
  facts: Readonly<Record<string, { allowsAdditionalDiscount: boolean; maxAdditionalDiscountPct: number | null }>>,
): number {
  let limit: number | null = null;
  for (const k of keys ?? []) {
    const key = (k ?? '').toString().trim();
    if (!key) continue;
    const f = facts[key];
    if (!f || f.allowsAdditionalDiscount === false || f.maxAdditionalDiscountPct == null) continue;
    const v = Number(f.maxAdditionalDiscountPct);
    if (!Number.isFinite(v)) continue;
    limit = limit === null ? v : Math.min(limit, v);
  }
  return limit ?? DEFAULT_ADDITIONAL_ALLOWANCE_PCT;
}

/** Per-product facts from POST /proposals/line-pricing. */
export interface LineFacts {
  allowsAdditionalDiscount: boolean;
  maxAdditionalDiscountPct: number | null;
  policyCategory: string | null;
  /** Actor-effective self-serve cap (Slab 1 ∧ product). */
  autoApprovePct: number | null;
  /** The product's own ceiling — the wall. */
  productMaxDiscountPct: number | null;
  discountApprovalEnabled: boolean;
}

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function factsFromLinePricing(map: Record<string, any> | null | undefined): Record<string, LineFacts> {
  const out: Record<string, LineFacts> = {};
  for (const [k, v] of Object.entries(map ?? {})) {
    out[k] = {
      allowsAdditionalDiscount: (v as any)?.allowsAdditionalDiscount !== false,
      maxAdditionalDiscountPct: numOrNull((v as any)?.maxAdditionalDiscountPct),
      policyCategory: typeof (v as any)?.policyCategory === 'string' && (v as any).policyCategory.trim() ? (v as any).policyCategory.trim() : null,
      autoApprovePct: numOrNull((v as any)?.autoApprovePct),
      productMaxDiscountPct: numOrNull((v as any)?.productMaxDiscountPct),
      discountApprovalEnabled: (v as any)?.discountApprovalEnabled !== false,
    };
  }
  return out;
}

const EPS = 1e-9;

export type LineDiscountVerdict = 'ok' | 'needs_approval' | 'refused';

export function lineDiscountVerdict(pct: number, f: Pick<LineFacts, 'autoApprovePct' | 'productMaxDiscountPct'> & { discountApprovalEnabled?: boolean } | null | undefined): LineDiscountVerdict {
  const d = Number(pct) || 0;
  if (d <= 0 || !f) return 'ok';
  if (f.productMaxDiscountPct != null && d - f.productMaxDiscountPct > EPS) return 'refused';
  if (f.discountApprovalEnabled === false) return 'ok';
  if (f.autoApprovePct != null && d - f.autoApprovePct > EPS) return 'needs_approval';
  return 'ok';
}

/** "Additional discount not allowed on <categories>" — null when every line allows it. */
export function additionalDiscountNotAllowedText(
  lines: ReadonlyArray<{ productId?: string | null; name: string }>,
  facts: Readonly<Record<string, Pick<LineFacts, 'allowsAdditionalDiscount' | 'policyCategory'>>>,
): string | null {
  const names: string[] = [];
  let blocked = false;
  for (const l of lines ?? []) {
    const f = l.productId ? facts[l.productId] : undefined;
    if (!f || f.allowsAdditionalDiscount !== false) continue;
    blocked = true;
    const n = (f.policyCategory ?? '').trim() || (l.name ?? '').trim();
    if (n && !names.includes(n)) names.push(n);
  }
  if (!blocked) return null;
  return names.length ? `Additional discount not allowed on ${names.join(', ')}` : 'Additional discount not allowed on this product';
}

/** 6.000000000000001 → "6"; 7.5 → "7.5". */
export const fmtPct = (n: number): string => String(Number((Number(n) || 0).toFixed(2)));

export function lineRefusedMessage(items: ReadonlyArray<{ name: string; pct: number; maxPct: number }>): string {
  const names = items.map((i) => `"${i.name}" (${fmtPct(i.pct)}% > max ${fmtPct(i.maxPct)}%)`).join(', ');
  return `Discount is above the maximum this product can take: ${names}. No approver can clear this — lower the discount.`;
}

export function orderDiscountOverLimitMessage(pct: number, limitPct: number): string {
  return `Additional discount of ${fmtPct(pct)}% exceeds the ${fmtPct(limitPct)}% limit for this order — refused, no approver can clear this.`;
}

/** Short inline label under the order-discount field. */
export const orderDiscountLimitNote = (limitPct: number): string =>
  `Up to ${fmtPct(limitPct)}% is allowed — no approval needed. Above it the order is refused.`;
